import { useQuery } from "@tanstack/react-query";
import { hasFeature, getFeatureLimit, getMinimumTierForFeature, type SubscriptionTier } from "@shared/subscriptionTiers";

interface TierResponse {
  tier: SubscriptionTier;
}

/**
 * Hook to fetch and check the current user's subscription tier.
 *
 * Mirrors usePermissions() architecture:
 * - Fetches from GET /api/auth/subscription-tier
 * - Caches for 5 minutes
 * - Returns helpers for feature checks
 *
 * @example
 * const { tier, hasFeature, isLoading } = useSubscriptionTier();
 *
 * if (hasFeature('email_ai')) { ... }
 * const limit = getLimit('email_campaigns'); // 150 | null
 */
export function useSubscriptionTier() {
  const { data, isLoading, error } = useQuery<TierResponse>({
    queryKey: ["/api/auth/subscription-tier"],
    queryFn: async () => {
      const response = await fetch("/api/auth/subscription-tier", {
        credentials: "include",
      });

      if (!response.ok) {
        if (response.status === 401) {
          return { tier: "standard" as SubscriptionTier };
        }
        throw new Error("Failed to fetch subscription tier");
      }

      return response.json();
    },
    retry: false,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  const tier = data?.tier ?? "standard";

  return {
    /** Current subscription tier */
    tier,
    isLoading,
    error,

    /**
     * Check if the current tier has access to a feature
     */
    hasFeature: (feature: string): boolean => {
      return hasFeature(tier, feature);
    },

    /**
     * Get the numeric limit for a feature, or null if unlimited
     */
    getLimit: (feature: string): number | null => {
      return getFeatureLimit(tier, feature);
    },

    /**
     * Get the minimum tier required for a feature
     */
    getRequiredTier: (feature: string): SubscriptionTier | null => {
      return getMinimumTierForFeature(feature);
    },
  };
}
